import * as React from 'react';
import { useEffect, useMemo } from 'react';
import classnames from '../../../core/utils/classnames.tsx';
import { withIcon } from '../../../core/components/Icon';
import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons';
import { useControllableState } from '../../../core/utils/useControllableState.tsx';
import { BaseButton } from '../../../core/components/Button';
import useIsMounted from '../../../core/utils/hooks/useIsMounted.tsx';

interface AssistanceSectionContextProps {
    viewMode: 'list' | 'table';
    enableDragDrop?: boolean;
}
const AssistanceSectionContext = React.createContext<AssistanceSectionContextProps>({
    viewMode: 'list',
    enableDragDrop: false,
});
export const useAssistanceSectionContext = () => React.useContext(AssistanceSectionContext);

const ToggleOpenIcon = withIcon(faChevronUp);
const ToggleCloseIcon = withIcon(faChevronDown);

const AssistanceSection = ({
    title,
    defaultOpen = true,
    open: controlledOpen,
    onOpenChange,
    headerClassnames = '',
    headerButtonClassnames = '',
    assistanceContentClassnames = '',
    controls,
    viewMode = 'list',
    enableDragDrop = false,
    hideChevron = false,
    children,
    ...props
}: any) => {
    const ref = React.useRef<HTMLDivElement>(null);
    const [isOpen, setIsOpen] = useControllableState(defaultOpen, controlledOpen, onOpenChange);

    const isMounted = useIsMounted();

    useEffect(() => {
        if (isMounted) {
            window.document.querySelector('.js-assistance-form')?.scrollTo({
                top: ref.current?.offsetTop || 0,
                behavior: 'smooth',
            });
        }
    }, [isOpen]);

    const contextValue = useMemo(
        () => ({
            viewMode,
            enableDragDrop,
        }),
        [viewMode, enableDragDrop]
    );

    return (
        <AssistanceSectionContext.Provider value={contextValue}>
            <div
                className={classnames(
                    'flex flex-col w-full border-b border-solid border-line-weak last:-mb-px relative',
                    isOpen && 'last:border-b-0'
                )}
                ref={ref}
                data-scroll-anchor="assistanceSection"
                {...props}
            >
                <div
                    className={classnames(
                        'flex justify-between border-l border-solid border-line-weak gap-4 items-center sticky top-0 z-20 bg-surface-weak p-6',
                        headerClassnames
                    )}
                >
                    {hideChevron ? (
                        <div
                            className={classnames(
                                'text-sm pl-[2px] flex gap-4 items-center h-8 pr-2.5 -ml-2.5 text-content-strong',
                                headerButtonClassnames
                            )}
                        >
                            <h3 className="text-sm font-medium min-w-0 flex-1">{title}</h3>
                        </div>
                    ) : (
                        <button
                            className={classnames(
                                'text-sm flex gap-4 items-center rounded transition-colors duration-200 hover:bg-surface-strong h-8 px-2.5 -ml-2.5 text-content-strong',
                                headerButtonClassnames
                            )}
                            onClick={() => setIsOpen(!isOpen)}
                        >
                            <h3 className="text-sm font-medium">{title}</h3>
                            {isOpen ? <ToggleOpenIcon /> : <ToggleCloseIcon />}
                        </button>
                    )}

                    {controls && <div className="flex gap-2 items-center">{controls}</div>}
                </div>
                <div
                    className={classnames('p-6 pt-0', !isOpen && !hideChevron && 'hidden', assistanceContentClassnames)}
                >
                    {children}
                </div>
            </div>
        </AssistanceSectionContext.Provider>
    );
};

const AssistanceSectionButtonSeparator = ({ className, ...props }: any) => {
    return <div className={classnames('h-6 border-l border-solid border-line-weak', className)} {...props} />;
};

const AssistanceSectionButton = ({ className, active = false, ...props }: any) => {
    return (
        <BaseButton
            className={classnames(
                'flex items-center justify-center gap-2 rounded transition-colors duration-200 hover:bg-surface-strong cursor-pointer text-sm font-medium text-content-strong hover:text-content-strong disabled:opacity-40 disabled:pointer-events-none data-[state=open]:bg-surface-brand-medium-default data-[state=open]:text-content-brand-strong',
                active &&
                    '!bg-surface-brand-medium-default !text-content-brand-strong hover:!bg-surface-brand-medium-hover active:!bg-surface-brand-medium-active',
                className
            )}
            {...props}
        />
    );
};

const AssistanceSectionIconButton = ({ className, ...props }: any) => {
    return (
        <AssistanceSectionButton className={classnames('h-8 w-8 items-center justify-center', className)} {...props} />
    );
};

const AssistanceFieldGroups = ({ className, children, viewMode = 'list' }: any) => {
    return { children };
};

const AssistanceFieldGroup = React.forwardRef<HTMLDivElement, any>(
    ({ className, title, required = false, controls, children, ...props }, ref) => {
        const { viewMode, enableDragDrop } = useAssistanceSectionContext();

        return (
            <div
                ref={ref}
                className={classnames(
                    'flex group',
                    viewMode === 'list' ? 'flex-col gap-2' : enableDragDrop ? 'gap-3' : 'gap-0.5',
                    className
                )}
                {...props}
            >
                <span
                    className={classnames(
                        'flex gap-2 items-center justify-between',
                        viewMode !== 'list' && controls.length > 0 && 'sticky left-0 z-10 bg-surface-weak p-px pr-2'
                    )}
                >
                    {viewMode === 'list' && (
                        <label className="text-sm font-medium text-content-strong">
                            {title} {required && <span className="text-content-danger-strong">*</span>}
                        </label>
                    )}

                    {controls && <span className="flex gap-1">{controls}</span>}
                </span>
                <div className={classnames('flex gap-2', viewMode === 'list' && 'flex-col')}>{children}</div>
            </div>
        );
    }
);
AssistanceFieldGroup.displayName = 'AssistanceFieldGroup';

const AssistanceFieldGroupButton = ({ className, active = false, ...props }: any) => {
    return (
        <BaseButton
            className={classnames(
                'flex px-2.5 items-center justify-center gap-2 rounded transition-colors hover:bg-surface-strong cursor-pointer text-sm font-medium text-content-strong hover:text-content-strong disabled:opacity-40 disabled:pointer-events-none aria-expanded:bg-surface-brand-medium-default',
                active &&
                    '!bg-surface-brand-medium-default !text-content-brand-strong hover:!bg-surface-brand-medium-hover active:!bg-surface-brand-medium-active',
                className
            )}
            type="button"
            {...props}
        />
    );
};

const AssistanceFieldGroupIconButton = ({ className, ...props }: any) => {
    return <AssistanceFieldGroupButton className={classnames('h-8 w-8 px-0', className)} {...props} />;
};

const AssistanceFieldGroupLoadingSkeleton = ({ className, lines = 3, ...props }: any) => {
    const { viewMode } = useAssistanceSectionContext();

    return (
        <div
            className={classnames('flex gap-3 animate-pulse', viewMode === 'list' && 'flex-col', className)}
            {...props}
        >
            {viewMode === 'list' && (
                <span className="text-sm font-medium flex-none flex gap-2 items-center justify-between">
                    <label className="rounded-full bg-content-medium h-2 w-32 block opacity-80" />
                </span>
            )}

            <div className={classnames('flex gap-2 flex-1', viewMode === 'list' && 'flex-col')}>
                {[...Array(lines)].map((_, i) => (
                    <div key={i} className="rounded-md h-8 w-full bg-surface-strong opacity-80" />
                ))}
            </div>
        </div>
    );
};

const AssistanceFieldLabel = ({ className, label, required = false, children, ...props }: any) => {
    const { viewMode } = useAssistanceSectionContext();

    return (
        <div className={classnames('flex gap-1', className)} {...props}>
            {viewMode === 'list' && (
                <label className="text-xs text-content-medium w-36 flex-none flex justify-start gap-1 py-2">
                    <span className="truncate">{label}</span>{' '}
                    {required && <span className="text-content-danger-strong">*</span>}
                </label>
            )}
            <div className={classnames('flex gap-1 flex-1 min-w-0', viewMode === 'list' && 'flex-col')}>{children}</div>
        </div>
    );
};

export default Object.assign(AssistanceSection, {
    Button: AssistanceSectionButton,
    IconButton: AssistanceSectionIconButton,
    ButtonSeparator: AssistanceSectionButtonSeparator,
    FieldGroup: AssistanceFieldGroup,
    FieldGroups: AssistanceFieldGroups,
    FieldGroupLoadingSkeleton: AssistanceFieldGroupLoadingSkeleton,
    FieldLabel: AssistanceFieldLabel,
    FieldGroupButton: AssistanceFieldGroupButton,
    FieldGroupIconButton: AssistanceFieldGroupIconButton,
});
